"use client"

import type React from "react"

import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, ChevronLeft, ChevronRight, ShoppingBag, Clock, Flame, Utensils, Plus } from "lucide-react"
import type { Category, Dish } from "@/lib/api"
import DishDetailModal from "./dish-detail-modal"
import { useRouter } from "next/navigation"
import { useApp } from "@/lib/context"
import AddToDraftButton from "./add-to-draft-button"
import PriceDisplay from "./price-display"
import type { SectionVariant } from "./categories-section"

interface CategoryModalProps {
  category: Category
  onClose: () => void
  variant?: SectionVariant
}

const MODAL_STYLES: Record<SectionVariant, {
  shell: string
  featuredTitle: string
  featuredCard: string
  featuredBadge: string
  featuredAdd: string
  listTitle: string
  listItem: string
  listImageHover: string
  listNameHover: string
}> = {
  default: {
    shell: "md:rounded-2xl",
    featuredTitle: "text-foreground",
    featuredCard: "bg-card rounded-xl shadow-md border-border",
    featuredBadge: "bg-primary text-primary-foreground",
    featuredAdd: "bg-primary text-primary-foreground hover:opacity-90",
    listTitle: "text-foreground",
    listItem: "bg-card rounded-xl border-border",
    listImageHover: "group-hover:border-primary",
    listNameHover: "group-hover:text-primary",
  },
  modern: {
    shell: "md:rounded-2xl",
    featuredTitle: "text-foreground",
    featuredCard: "bg-white rounded-xl shadow-md border-gray-100",
    featuredBadge: "bg-gradient-to-r from-orange-500 to-orange-600",
    featuredAdd: "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700",
    listTitle: "text-foreground",
    listItem: "bg-gradient-to-r from-white to-gray-50 rounded-xl border-gray-100",
    listImageHover: "group-hover:border-orange-500",
    listNameHover: "group-hover:text-orange-600",
  },
  vibrant: {
    shell: "md:rounded-3xl",
    featuredTitle: "text-foreground",
    featuredCard: "bg-white rounded-2xl shadow-md border-gray-100",
    featuredBadge: "bg-gradient-to-r from-purple-500 to-pink-500",
    featuredAdd: "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600",
    listTitle: "text-foreground",
    listItem: "bg-gradient-to-r from-purple-50/60 via-white to-cyan-50/60 rounded-xl border-gray-100",
    listImageHover: "group-hover:border-purple-500",
    listNameHover: "group-hover:text-purple-600",
  },
  minimal: {
    shell: "md:rounded-none",
    featuredTitle: "text-foreground uppercase tracking-wider",
    featuredCard: "bg-white rounded-none border-foreground/10",
    featuredBadge: "bg-foreground",
    featuredAdd: "bg-foreground hover:bg-black",
    listTitle: "text-foreground uppercase tracking-wider",
    listItem: "bg-white rounded-none border-foreground/10",
    listImageHover: "group-hover:border-foreground",
    listNameHover: "group-hover:text-foreground",
  },
}

export default function CategoryModal({ category, onClose, variant = "default" }: CategoryModalProps) {
  const s = MODAL_STYLES[variant]
  const router = useRouter()
  const { t, addToDraft, draftCount } = useApp()
  const [selectedDish, setSelectedDish] = useState<Dish | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)

  const handleDishClick = (dish: Dish) => {
    setSelectedDish(dish)
  }

  const handleCloseDishModal = () => {
    setSelectedDish(null)
  }

  const handleAddToDraft = (e: React.MouseEvent, dish: Dish) => {
    e.stopPropagation()
    addToDraft(dish)
  }

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -280, behavior: "smooth" })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 280, behavior: "smooth" })
    }
  }

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setShowLeftArrow(scrollLeft > 0)
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10)
    }
  }

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkScrollPosition)
      checkScrollPosition()

      return () => {
        scrollContainer.removeEventListener("scroll", checkScrollPosition)
      }
    }
  }, [])
  // Função para fechar modal com escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [onClose])

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
            className={`fixed inset-0 z-50 flex items-start justify-center overflow-y-auto backdrop-blur-sm ${
              variant === "modern" ? "bg-black/85" : "bg-black/70"
            }`}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className={`bg-white w-full min-h-screen md:min-h-0 md:max-h-[95vh] md:max-w-5xl md:my-2 overflow-hidden shadow-2xl ${s.shell}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Cabeçalho Responsivo */}
          <div className="sticky top-0 z-10 w-full">
            <div className="relative h-32 sm:h-40 md:h-48 overflow-hidden">
              <img
                src={category.urls[0] || "/placeholder.svg"}
                alt={category.nome}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = "/placeholder.svg"
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

              {/* Controles do Cabeçalho - Responsivos */}
              <div className="absolute top-3 right-3 flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    router.push("/pedido")
                  }}
                  className="bg-white/20 backdrop-blur-md rounded-full p-2 sm:p-3 text-white hover:bg-white/30 transition-all duration-300 relative shadow-lg touch-manipulation"
                  aria-label={t("viewDraft")}
                >
                  <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5" />
                  {draftCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-hotel-orange text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center shadow-lg">
                      {draftCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="bg-white/20 backdrop-blur-md rounded-full p-2 sm:p-3 text-white hover:bg-white/30 transition-all duration-300 shadow-lg touch-manipulation"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>

              {/* Título e Informações - Responsivos */}
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 md:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center">
                    <Utensils className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">{category.nome}</h2>
                    <div className="flex items-center gap-2 sm:gap-3 text-white/80 mt-1 text-xs sm:text-sm">
                      <span className="flex items-center gap-1">
                        {category.pratos.length} {t("items")}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {Math.min(...category.pratos.map((dish) => dish.tempo_preparo || 15))} min
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Carrossel Horizontal - Responsivo */}
          <div className="relative px-3 sm:px-4 md:px-6 py-4 sm:py-6 border-b border-gray-100">
            <h3 className={`text-lg sm:text-xl font-bold mb-3 sm:mb-4 flex items-center gap-2 ${s.featuredTitle}`}>
              <Utensils className="w-4 h-4 sm:w-5 sm:h-5 text-hotel-orange" />
              <span className="text-sm sm:text-base">Destaques da Categoria</span>
            </h3>

            <div className="relative">
              {showLeftArrow && (
                <button
                  onClick={scrollLeft}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 text-gray-800 p-2 rounded-full shadow-lg border border-gray-200 transition-all duration-300 touch-manipulation"
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}

              <div ref={scrollContainerRef} className="flex overflow-x-auto gap-3 sm:gap-4 py-2 px-1 hide-scrollbar">
                {category.pratos
                  .filter((dish) => dish.destacado)
                  .map((dish) => (
                    <motion.div
                      key={dish._id}
                      whileHover={{ y: -4 }}
                      className={`flex-shrink-0 w-64 sm:w-72 overflow-hidden border hover:shadow-xl transition-all duration-300 touch-manipulation cursor-pointer ${s.featuredCard}`}
                      onClick={() => handleDishClick(dish)}
                    >
                      <div className="relative h-32 sm:h-40 overflow-hidden">
                        <img
                          src={dish.urls[0] || "/placeholder.svg"}
                          alt={dish.nome}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                          onError={(e) => {
                            e.currentTarget.src = "/placeholder.svg"
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                        {dish.destacado && (
                          <div className={`absolute top-2 left-2 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 shadow-md ${s.featuredBadge}`}>
                            <Flame className="w-2 h-2" />
                            <span>{t("featured")}</span>
                          </div>
                        )}
                        {dish.emPromocao && (
                          <div className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide shadow-md">
                            Promoção
                          </div>
                        )}
                      </div>

                      <div className="p-3 sm:p-4">
                        <h4 className="font-bold text-gray-800 text-sm sm:text-base mb-1 sm:mb-2 line-clamp-1">
                          {dish.nome}
                        </h4>
                        <p className="text-gray-600 text-xs sm:text-sm line-clamp-2 mb-2 sm:mb-3 leading-relaxed">
                          {dish.descricao}
                        </p>

                        <div className="flex items-center justify-between">
                          <div>
                            <PriceDisplay dish={dish} />
                            <div className="flex items-center gap-1 text-gray-500 text-xs mt-1">
                              <Clock className="w-2 h-2 sm:w-3 sm:h-3" />
                              <span>{dish.tempo_preparo || 15} min</span>
                            </div>
                          </div>

                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => handleAddToDraft(e, dish)}
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white transition-all duration-300 shadow-md touch-manipulation ${s.featuredAdd}`}
                          >
                            <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
              </div>

              {showRightArrow && (
                <button
                  onClick={scrollRight}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 text-gray-800 p-2 rounded-full shadow-lg border border-gray-200 transition-all duration-300 touch-manipulation"
                  aria-label="Scroll right"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Lista Completa - Responsiva */}
          <div className="p-3 sm:p-4 md:p-6 pb-16 sm:pb-20">
            <h3 className={`text-lg sm:text-xl font-bold mb-3 sm:mb-4 flex items-center gap-2 ${s.listTitle}`}>
              <Utensils className="w-4 h-4 sm:w-5 sm:h-5 text-hotel-orange" />
              <span className="text-sm sm:text-base">Todos os Pratos</span>
            </h3>

            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              {category.pratos.map((dish, index) => (
                <motion.div
                  key={dish._id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className={`p-3 sm:p-4 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer group border touch-manipulation ${s.listItem}`}
                  onClick={() => handleDishClick(dish)}
                >
                  <div className="flex gap-3 sm:gap-4">
                    <motion.div
                      className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-shrink-0 border-2 border-transparent transition-all duration-300 ${s.listImageHover}`}
                      whileHover={{ scale: 1.05 }}
                    >
                      <img
                        src={dish.urls[0] || "/placeholder.svg"}
                        alt={dish.nome}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = "/placeholder.svg"
                        }}
                      />
                    </motion.div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1 sm:mb-2">
                        <h4 className={`text-sm sm:text-base font-bold transition-colors line-clamp-1 flex-1 ${s.listNameHover}`}>
                          {dish.nome}
                        </h4>
                        {dish.destacado && <Flame className="w-4 h-4 text-hotel-gold flex-shrink-0 ml-2" />}
                      </div>

                      <p className="text-gray-600 text-xs sm:text-sm line-clamp-2 mb-2 sm:mb-3 leading-relaxed">
                        {dish.descricao}
                      </p>

                      <div className="flex items-center justify-between">
                        <div>
                          <PriceDisplay dish={dish} className="text-base sm:text-lg font-bold text-hotel-orange" />
                          <div className="flex items-center gap-1 text-gray-500 text-xs">
                            <Clock className="w-2 h-2 sm:w-3 sm:h-3" />
                            <span>{dish.tempo_preparo || 15} min</span>
                          </div>
                        </div>

                        <AddToDraftButton dish={dish} onClick={(e) => handleAddToDraft(e, dish)} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {selectedDish && <DishDetailModal dish={selectedDish} onClose={handleCloseDishModal} />}
    </AnimatePresence>
  )
}
